import type { ServiceValidationResult, ValidateUserRequest } from '../types';
import type { HttpClient } from './http';
import { isUserNotFoundError } from './user-not-found';

interface ValidationResponse {
  auth: string[];
  firstName: string;
  theme: Record<string, { user_id?: number; acceptance?: boolean }>;
}

interface ValidationErrorResponse {
  error: string;
}

/**
 * Calls the client-app `next_steps` API to determine whether a user exists in the
 * Rails DB and, if so, which auth methods are available.
 *
 * Shared by both {@link ClientAppAuthService} and {@link CognitoAuthService}
 * — the endpoint is infrastructure-agnostic.
 */
export async function fetchClientAppUserStatus(
  http: HttpClient,
  request: ValidateUserRequest,
): Promise<ServiceValidationResult> {
  const params = new URLSearchParams();
  if (request.email) params.set('email', request.email);
  if (request.userId) params.set('userId', request.userId);
  if (request.provider) params.set('provider', request.provider);

  let response: ValidationResponse | ValidationErrorResponse;
  try {
    response = await http.request<ValidationResponse | ValidationErrorResponse>(
      `/services/api/v4/auth/next_steps?${params.toString()}`,
      'GET',
    );
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (isUserNotFoundError(message)) {
      return { isExisting: false, authMethods: [], firstName: '', themeData: {} };
    }
    throw err;
  }

  if ('error' in response) {
    if (isUserNotFoundError(response.error)) {
      return { isExisting: false, authMethods: [], firstName: '', themeData: {} };
    }
    throw new Error(response.error);
  }

  return {
    isExisting: response.auth.length > 0,
    authMethods: response.auth,
    firstName: response.firstName,
    themeData: response.theme,
  };
}
